//#region Imports
import { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { Card, Alert, Row, Col, Form, InputGroup, Table, Spinner, Dropdown } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../configs/apiConfig';
import { LanguageContext } from '../../contexts/LanguageProvider';
import { FaSearch } from "react-icons/fa";
import PaginationComponent from '../PaginationComponent';
import translations from './translations';
import useMediaQuery from '../../utils/useMediaQuery';
import { useAlertContext } from '../../contexts/AlertContext';
//#endregion

//#region Componente Principal
function RoleChanging() {
  //#region Declarações
  const [users, setUsers] = useState([]);
  const [nameFilter, setNameFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [loadingData, setLoadingData] = useState(false);

  const { role: userRole } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);

  const isMobile = useMediaQuery('(max-width: 768px)');
  const { showSuccess, showError } = useAlertContext();

  // Seleciona as traduções do idioma atual
  const t = translations[language] || translations.pt;
  //#endregion

  //#region Efeitos (useEffect)
  useEffect(() => {
    setLoadingData(true);
    // Fetch dos usuários
    const fetchUsers = async () => {
      try {
        const auth = localStorage.getItem('auth');
        const response = await axios.get(API_ENDPOINTS.USERS, {
          headers: { 'Authorization': `Basic ${auth}` }
        });
        setUsers(response.data);
      } catch (error) {
        showError(t.failedToLoadUsers + error.message);
      } finally {
        setLoadingData(false);
      }
    };

    if (userRole === 'ADMIN') {
      fetchUsers();
    }
  }, [userRole, t.failedToLoadUsers, showError]);

  // Reset para primeira página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [nameFilter, roleFilter]);
  //#endregion


  //#region Funções Auxiliares
  const handleRoleChange = async (userId, newRole) => {
    try {
      const auth = localStorage.getItem('auth');
      await axios.put(
        API_ENDPOINTS.USERS_BY_ID_ROLE(userId),
        newRole,
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'text/plain'
          }
        }
      );

      setUsers(users.map(user =>
        user.id === userId ? { ...user, role: newRole } : user
      ));
      showSuccess(`${t.roleUpdatedTo}${newRole} (${t.idPrefix}${userId})`);
    } catch (error) {
      showError(t.failedToUpdateRoleConflict);
    }
  };

  const getAvailableRoles = (currentRole) => {
    const roles = {
      'READER': ['EDITOR', 'ADMIN'],
      'EDITOR': ['READER', 'ADMIN'],
      'ADMIN': ['READER', 'EDITOR'],
    };
    return roles[currentRole] || [];
  };

  const roleClass = (role) => `role-${(role || '').toLowerCase()}`;
  const userInitial = (user) =>
    ((user.name || user.username || '?').trim()[0] || '?').toUpperCase();

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset para primeira página
  };
  //#endregion

  //#region Funções de Filtro
  // Get unique role types for filter dropdown
  const uniqueRoles = [...new Set(users.map(user => user.role))].sort();

  // Filter users based on name and role filters
  const filteredUsers = users.filter(user => {
    const nameMatch = (user.name || '').toLowerCase().includes((nameFilter || '').toLowerCase());
    const roleMatch = roleFilter === '' || user.role === roleFilter;
    return nameMatch && roleMatch;
  });
  //#endregion

  //#region Lógica de Paginação
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);
  //#endregion

  //#region Renderização
  return (
    <div className="container mt-4 p-1">
      <Card>
        <Card.Header as="h2">
          {t.manageUsers}
        </Card.Header>
        <Card.Body className="p-2">
          {userRole === 'ADMIN' ? (
            <>
              {/* Filters */}
              <Row className="mb-3">
                <Col md={4} className="mb-2 mb-md-0">
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder={t.filterByName}
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={4} className="mb-2 mb-md-0">
                  <Dropdown className="w-100" onSelect={(val) => setRoleFilter(val ?? '')}>
                    <Dropdown.Toggle as="button" type="button" className="form-select text-start filter-select">
                      {roleFilter === '' ? t.allRoles : roleFilter}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      <Dropdown.Item eventKey="" active={roleFilter === ''}>{t.allRoles}</Dropdown.Item>
                      {uniqueRoles.map(role => (
                        <Dropdown.Item eventKey={role} key={role} active={roleFilter === role}>{role}</Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
                <Col md={4}>
                  <Dropdown className="w-100" onSelect={(val) => handleItemsPerPageChange(Number(val))}>
                    <Dropdown.Toggle as="button" type="button" className="form-select text-start filter-select">
                      {itemsPerPage === 5 ? t.fivePerPage
                        : itemsPerPage === 25 ? t.twentyFivePerPage
                        : itemsPerPage === 50 ? t.fiftyPerPage
                        : t.tenPerPage}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      <Dropdown.Item eventKey="5" active={itemsPerPage === 5}>{t.fivePerPage}</Dropdown.Item>
                      <Dropdown.Item eventKey="10" active={itemsPerPage === 10}>{t.tenPerPage}</Dropdown.Item>
                      <Dropdown.Item eventKey="25" active={itemsPerPage === 25}>{t.twentyFivePerPage}</Dropdown.Item>
                      <Dropdown.Item eventKey="50" active={itemsPerPage === 50}>{t.fiftyPerPage}</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              </Row>

              {/* Informações de paginação */}
              <Row className="mb-3">
                <Col>
                  <small className="text-muted">
                    {t.showing} {startIndex + 1} {t.to} {Math.min(endIndex, filteredUsers.length)} {t.of} {filteredUsers.length} {t.users}
                    {filteredUsers.length !== users.length && ` (filtrados de ${users.length} total)`}
                  </small>
                </Col>
              </Row>
              {loadingData ? (
                <div className="text-center py-3">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t.loading}</span>
                  </Spinner>
                </div>
              ) : (
                <div className="table-responsive" style={{ overflow: 'visible' }}>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th style={{ minWidth: '60px' }}>{t.idColumn}</th>
                        <th style={{ minWidth: '120px' }}>{t.user}</th>
                        <th style={{ minWidth: '150px' }}>{t.name}</th>
                        <th style={{ minWidth: '120px' }}>{t.currentRole}</th>
                        <th style={{ minWidth: '150px' }}>{t.newRole}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentUsers.length > 0 ? (
                        currentUsers.map(user => (
                          <tr key={user.id}>
                            <td>{user.id}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span className={`user-avatar ${roleClass(user.role)}`}>{userInitial(user)}</span>
                                <span className="fw-semibold">{user.username}</span>
                              </div>
                            </td>
                            <td>{user.name ? user.name : <span className="text-muted">—</span>}</td>
                            <td>
                              <span className={`badge role-badge ${roleClass(user.role)}`}>{user.role}</span>
                            </td>
                            <td>
                              <Dropdown onSelect={(val) => handleRoleChange(user.id, val)}>
                                <Dropdown.Toggle
                                  as="button"
                                  type="button"
                                  className="form-select form-select-sm text-start filter-select"
                                  style={{ minWidth: '130px' }}
                                >
                                  {user.role}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  {getAvailableRoles(user.role).map(role => (
                                    <Dropdown.Item eventKey={role} key={role}>{role}</Dropdown.Item>
                                  ))}
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-3 text-muted">
                            {t.noResults}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>)}

              {/* Controles de paginação */}
              {totalPages > 1 && (
                <PaginationComponent
                  totalPages={totalPages}
                  isMobile={isMobile}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  t={t}
                  tItemName={t.users}
                  indexOfFirst={startIndex}
                  indexOfLast={Math.min(endIndex, filteredUsers.length)}
                  lengthTotal={filteredUsers.length}
                />
              )}
            </>
          ) : (
            <Alert variant="warning">
              {t.adminOnly}
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  );
  //#endregion
}
//#endregion

export default RoleChanging;